import {showToast, ToastrHelper} from '$lib/helpers/toastrHelper';
import {
    ColumnNames,
    type CreateClientNoteRequest,
    type CreateClientRequest,
    type CreateClientRequestStore,
    type DeleteClientNoteRequest,
    type GetClientByIdResponse,
    type OrganisationSearchResult,
    type PutClientRequest
} from '$lib/models/client/types';
import type {IdName} from '$lib/models/types';
import {type BaseListResponse, DateFilters, type ErrorResponse, SortBy} from '$lib/models/types';
import {fetchWithAuth} from '$lib/utils/http/httpUtil';
import {extractErrorMessage} from '$lib/utils/error/errorUtils';
import type {State} from '@vincjo/datatables/remote';

function createClientStore() {
  const type: string = 'Client';
  const baseUrl: string = '/api/client';

  return {
    fetchById: async (id: number): Promise<GetClientByIdResponse | ErrorResponse> => {
      const response = await fetchWithAuth(`${baseUrl}/${id}`, {
        method: 'GET',
      });

      if (response.status == 204) {
        const errorMessage = ToastrHelper.notFound(type);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to fetch ${type} (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }

      try {
        return await response.json();
      } catch (error) {
        const errorMessage = ToastrHelper.errorRetrieving(type);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
        };
      }
    },
    fetch: async (state: State): Promise<BaseListResponse> => {
      const { pageNumber, rowsPerPage, sort, filters } = state;

      let url = baseUrl;

      const sortBy: string =
        sort?.direction?.toString() != undefined ? (sort?.direction?.toString().toLowerCase() == 'asc' ? SortBy.Ascending : SortBy.Descending) : SortBy.Descending;

      url += `?pageNumber=${pageNumber}&pageSize=${rowsPerPage}&sortBy=${sortBy}&orderBy=${sort?.orderBy}`;
      if (filters) {
        url += filters
          .map(({ filterBy, value }) => {
            if (filterBy == ColumnNames.CreateDate) {
              const date = new Date();

              if (value === DateFilters.LastSevenDays) {
                date.setDate(date.getDate() - 7);
                return `&${filterBy}=${date.toISOString()}`;
              }
              if (value === DateFilters.LastFourteenDays) {
                date.setDate(date.getDate() - 14);
                return `&${filterBy}=${date.toISOString()}`;
              }
              if (value === DateFilters.LastMonth) {
                date.setMonth(date.getMonth() - 1);
                return `&${filterBy}=${date.toISOString()}`;
              }

              if (value == DateFilters.LastQuarter) {
                date.setMonth(date.getMonth() - 3);
                return `&${filterBy}=${date.toISOString()}`;
              }

              if (value == DateFilters.LastTwelveMonths) {
                date.setFullYear(date.getFullYear() - 1);
                return `&${filterBy}=${date.toISOString()}`;
              }

              return `&${filterBy}=${date.toISOString()}`;
            }

            return `&${filterBy}=${value}`;
          })
          .join('');
      }

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to fetch clients (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return { total: 0, data: [] };
      }

      try {
        return await response.json();
      } catch (error) {
        showToast({
          title: 'Error',
          message: 'Failed to load client data',
          type: 'error'
        });
        return { total: 0, data: [] };
      }
    },
    update: async (request: PutClientRequest): Promise<void> => {
      const response = await fetchWithAuth(`${baseUrl}/${request.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (response.ok) {
        return;
      } else {
        const errorMessage = await extractErrorMessage(response, `Failed to update client (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }
    },
    create: async (storeRequest: CreateClientRequestStore): Promise<void> => {
      const request: CreateClientRequest = {
        clientName: storeRequest.clientName,
        clientStatus: storeRequest.clientStatus,
        connectionTypeId: storeRequest.connectionTypeId,
        exportTypeId: storeRequest.exportTypeId,
        aliiOCRType: storeRequest.aliiOCRType,
        initialUserDetails: storeRequest.initialUserDetails,
        domains: storeRequest.domains,
        menuConfigs: storeRequest.menuConfigs.filter((config) => config.enabled).map((config) => config.name),
        organisationId: storeRequest.createNewOrganisation ? undefined : storeRequest.organisationId,
        organisationName: storeRequest.createNewOrganisation ? storeRequest.organisationName : undefined,
        location: storeRequest.location,
      };

      const response = await fetchWithAuth(baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (response.ok) {
        return;
      } else {
        const errorMessage = await extractErrorMessage(response, `Failed to create client (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }
    },
    createClientNote: async (storeRequest: CreateClientNoteRequest): Promise<void> => {
      const request: CreateClientNoteRequest = {
        id: storeRequest.id,
        noteDescription: storeRequest.noteDescription,
        noteCreator: storeRequest.noteCreator,
        clientId: storeRequest.clientId,
      };

      const response = await fetchWithAuth(`${baseUrl}/${request.clientId}/note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (response.ok) {
        return;
      } else {
        const errorMessage = await extractErrorMessage(response, `Failed to create client note (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }
    },
    deleteClientNote: async (clientId: number, noteId: number, currentUserDisplayName: string): Promise<void> => {
      const request: DeleteClientNoteRequest = {
        id: noteId,
        clientId: clientId,
        currentUserDisplayName: currentUserDisplayName
      };

      const response = await fetchWithAuth(`${baseUrl}/${clientId}/note/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (response.ok) {
        return;
      } else {
        // Use the centralized error message extraction
        const errorMessage = await extractErrorMessage(response, `Failed to delete client note (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }
      },
    searchOrganisations: async (search: string): Promise<OrganisationSearchResult[]> => {
      try {
        const response = await fetchWithAuth(`/api/organisation?search=${encodeURIComponent(search)}`, {
          method: 'GET',
        });
        if (!response.ok) return [];
        return await response.json();
      } catch {
        return [];
      }
    },
    getLocations: async (): Promise<IdName[]> => {
        const response = await fetchWithAuth(`${baseUrl}/GetLocations`, {
              method: 'GET',
          });

          if (!response.ok) {
              const errorMessage = `Failed to fetch locations (HTTP ${response.status})`;
              showToast({
                  title: 'Error',
                  message: errorMessage,
                  type: 'error'
              });
              return [];
          }

          try {
              const data = await response.json();
              return data;
          } catch (error) {
              showToast({
                  title: 'Error',
                  message: 'Failed to load locations',
                  type: 'error'
              });
              return [];
          }
      },
    getLanguages: async (): Promise<IdName[]> => {
        const response = await fetchWithAuth(`${baseUrl}/GetLanguages`, {
              method: 'GET',
          });

          if (!response.ok) {
              const errorMessage = `Failed to fetch languages (HTTP ${response.status})`;
              showToast({
                  title: 'Error',
                  message: errorMessage,
                  type: 'error'
              });
              return [];
          }

          try {
              const data = await response.json();
              return data;
          } catch (error) {
              showToast({
                  title: 'Error',
                  message: 'Failed to load languages',
                  type: 'error'
              });
              return [];
          }
      },
  };
}

export const clientStore = createClientStore();
